import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { Reveal, useSeo } from "@/components/site/Motion";
import { site } from "@/content/site";

const Item = ({ text, i, checked, toggle }) => (
  <li>
    <button type="button" onClick={toggle} aria-pressed={checked} className={`w-full text-left flex gap-5 p-5 lg:p-6 border transition-colors ${checked ? "bg-[#EEF3EF] border-[var(--emerald)]" : "bg-white border-[var(--line)] hover:border-[#BFB5A5]"}`} data-testid={`checklist-item-${i}`}>
      <span className={`mt-0.5 w-7 h-7 shrink-0 grid place-items-center border transition-colors ${checked ? "bg-[var(--emerald)] border-[var(--emerald)] text-white" : "border-[#A79D8E]"}`}>
        {checked && <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}><Check className="w-4 h-4" /></motion.span>}
      </span>
      <span className="leading-relaxed">{text}</span>
    </button>
  </li>
);

export default function Submissions() {
  const { journal } = useOutletContext();
  useSeo(`Submissions · ${journal.title}`, site.checklistIntro);
  const [done, setDone] = useState([]);
  const toggle = (i) => setDone((d) => (d.includes(i) ? d.filter((x) => x !== i) : [...d, i]));
  const all = done.length === site.checklist.length;

  return (
    <div className="space-y-16" data-testid="submissions-page">
      <Reveal>
        <p className="eyebrow mb-5">About · Submissions</p>
        <h1 className="font-display text-5xl sm:text-6xl leading-[1.02] mb-6">Submissions</h1>
        <p className="text-lg text-[var(--ink-muted)]">
          <Link to="/login" className="text-[var(--emerald)] underline underline-offset-4" data-testid="submissions-login-link">Login</Link> or{" "}
          <Link to="/register" className="text-[var(--emerald)] underline underline-offset-4" data-testid="submissions-register-link">Register</Link> to make a submission.
        </p>
      </Reveal>

      <Reveal>
        <div className="flex items-end justify-between gap-6 mb-6">
          <h2 className="font-display text-4xl">Submission Preparation Checklist</h2>
          <span className="font-mono-x text-sm text-[var(--emerald)] shrink-0" data-testid="checklist-progress">{done.length}/{site.checklist.length}</span>
        </div>
        <p className="text-[var(--ink-muted)] leading-relaxed mb-8">{site.checklistIntro}</p>
        <div className="h-1 bg-[var(--line)] mb-6"><motion.div className="h-full bg-[var(--emerald)]" animate={{ width: `${(done.length / site.checklist.length) * 100}%` }} /></div>
        <ol className="space-y-3">{site.checklist.map((t, i) => <Item key={i} i={i} text={t} checked={done.includes(i)} toggle={() => toggle(i)} />)}</ol>
        <div className={`mt-8 p-6 lg:p-8 flex flex-col sm:flex-row gap-5 sm:items-center justify-between transition-colors ${all ? "bg-[var(--emerald)] text-[var(--paper)]" : "bg-[var(--paper-2)]"}`} data-testid="checklist-result">
          <p className="font-display text-2xl">{all ? "Your manuscript is ready to submit." : "Tick every item to confirm your manuscript is ready."}</p>
          <Link to="/register" className={`inline-flex items-center gap-2 h-12 px-6 rounded-full shrink-0 transition-colors ${all ? "bg-[var(--paper)] text-[var(--emerald)] hover:bg-[var(--gold)]" : "border border-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--paper)]"}`} data-testid="checklist-continue-button">
            Continue to submission <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </Reveal>

      <Reveal className="grid sm:grid-cols-2 gap-8 border-t border-[var(--line)] pt-12">
        <div>
          <h2 className="font-display text-3xl mb-3">Articles</h2>
          <p className="text-[var(--ink-muted)]">{site.sections[0].policy}</p>
        </div>
        <div id="privacy">
          <h2 className="font-display text-3xl mb-3">Privacy Statement</h2>
          <p className="text-[var(--ink-muted)] leading-relaxed">{site.privacy}</p>
        </div>
      </Reveal>
    </div>
  );
}
